import React, { useState, useEffect } from "react";
import { Card, Form, Button, Alert, Modal, InputGroup } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faTimes } from '@fortawesome/free-solid-svg-icons';
import { collection, query, getDocs, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';

export default () => {
    const [targetType, setTargetType] = useState('everyone'); // 'everyone', 'specific'
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [showUserModal, setShowUserModal] = useState(false);

    const [messageText, setMessageText] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const q = query(collection(db, 'users'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const allUsers = [];
            snapshot.forEach(doc => {
                const data = doc.data();
                // Filter out test and fake accounts for the actual selection
                if (!data.isTestUser && !data.isFake && !data.isAdmin) {
                    allUsers.push({
                        id: doc.id,
                        name: data.name || 'İsimsiz',
                        profileImageUrl: data.profileImageUrl || null
                    });
                }
            });
            allUsers.sort((a, b) => a.name.localeCompare(b.name));
            setUsers(allUsers);
            setFilteredUsers(allUsers);
        }, (err) => {
            setError("Kullanıcılar yüklenirken hata oluştu: " + err.message);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredUsers(users);
            return;
        }
        const lower = searchTerm.toLowerCase().trim();
        const filtered = users.filter(u => u.name.toLowerCase().includes(lower));
        setFilteredUsers(filtered);
    }, [searchTerm, users]);

    const handleSendMessage = async () => {
        if (!messageText.trim()) {
            setError("Lütfen bir mesaj metni girin.");
            return;
        }

        if (targetType === 'specific' && !selectedUser) {
            setError("Lütfen bir kullanıcı seçin.");
            return;
        }

        setSending(true);
        setError(null);
        setSuccess(false);

        try {
            const sendAdminMessagesFn = httpsCallable(functions, 'sendAdminMessages');
            const result = await sendAdminMessagesFn({
                targetType: targetType,
                targetUserId: targetType === 'specific' ? selectedUser.id : null,
                messageText: messageText.trim()
            });

            const response = result.data;
            if (response.success) {
                setSuccess(true);
                setMessageText("");
                if (targetType === 'everyone') {
                    alert(`Kampanya mesajı arka planda gönderiliyor!`);
                } else {
                    alert(`Mesaj başarıyla gönderildi!`);
                }
            } else {
                throw new Error(response.message || 'Bilinmeyen hata');
            }
        } catch (err) {
            console.error("Error sending admin message:", err);
            setError("Mesaj gönderilirken hata oluştu: " + (err.message || err.toString()));
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <div className="py-4">
                <h4>Admin Mesajları (JİGOGO)</h4>
                <p className="text-muted mb-0">"JİGOGO" resmi hesabından sistem ve kampanya mesajları gönderin.</p>
            </div>

            {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert variant="success" dismissible onClose={() => setSuccess(false)}>Mesaj başarıyla gönderildi!</Alert>}

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <div className="mb-4">
                        <Form.Label className="fw-bold mb-3">Kime Gönderilsin?</Form.Label>
                        <div className="d-flex gap-3">
                            <Form.Check
                                type="radio"
                                id="target-everyone"
                                label="Herkese Gönder (Tüm Üyeler)"
                                checked={targetType === 'everyone'}
                                onChange={() => setTargetType('everyone')}
                            />
                            <Form.Check
                                type="radio"
                                id="target-specific"
                                label="Belirli Kullanıcıya Gönder"
                                checked={targetType === 'specific'}
                                onChange={() => setTargetType('specific')}
                            />
                        </div>
                    </div>

                    {targetType === 'specific' && (
                        <div className="mb-4">
                            {selectedUser ? (
                                <div className="d-flex align-items-center gap-2 p-3 bg-light rounded" style={{ maxWidth: '400px' }}>
                                    {selectedUser.profileImageUrl ? (
                                        <img src={selectedUser.profileImageUrl} alt="user" className="rounded-circle" style={{ width: 40, height: 40, objectFit: 'cover' }} />
                                    ) : (
                                        <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                                            <span style={{ color: '#fff' }}>{selectedUser.name.charAt(0)}</span>
                                        </div>
                                    )}
                                    <span className="flex-grow-1 fw-bold">{selectedUser.name}</span>
                                    <Button variant="link" onClick={() => setSelectedUser(null)}><FontAwesomeIcon icon={faTimes} className="text-danger" /></Button>
                                </div>
                            ) : (
                                <Button variant="outline-primary" onClick={() => setShowUserModal(true)}>Kullanıcı Seç</Button>
                            )}
                        </div>
                    )}

                    <div className="mb-4">
                        <Form.Label className="fw-bold">Mesaj Metni</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            placeholder="Kullanıcılara iletilecek mesajınızı buraya yazın..."
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                        />
                        <Form.Text className="text-muted">Bu mesaj <strong>JİGOGO</strong> adlı sanal bir profil tarafından gönderilecektir. Kullanıcılar yanıt veremez.</Form.Text>
                    </div>

                    <Button variant="primary" onClick={handleSendMessage} disabled={sending || (targetType === 'specific' && !selectedUser) || !messageText}>
                        <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                        {sending ? "Gönderiliyor..." : "Mesajı Gönder"}
                    </Button>
                </Card.Body>
            </Card>

            <Modal show={showUserModal} onHide={() => setShowUserModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Kullanıcı Seçin</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <InputGroup className="mb-3">
                        <Form.Control type="text" placeholder="İsim ile ara..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </InputGroup>
                    <div className="d-flex flex-wrap gap-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {filteredUsers.map((user) => (
                            <div key={user.id} className="d-flex flex-column align-items-center p-2 border rounded" style={{ cursor: 'pointer', width: '120px' }} onClick={() => { setSelectedUser(user); setShowUserModal(false); setSearchTerm(''); }}>
                                {user.profileImageUrl ? (
                                    <img src={user.profileImageUrl} alt="" className="rounded-circle mb-2" style={{ width: 50, height: 50, objectFit: 'cover' }} />
                                ) : (
                                    <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center mb-2" style={{ width: 50, height: 50 }}>
                                        <span style={{ color: '#fff' }}>{user.name.charAt(0)}</span>
                                    </div>
                                )}
                                <span className="text-center small">{user.name}</span>
                            </div>
                        ))}
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};
